import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Form, Button, Badge, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ADMIN_USERS, GET_ESTIMATIONS, SAVE_ESTIMATION, DELETE_ESTIMATION } from '../../graphql/operations';
import CustomerModal from './CustomerModal';

interface ProductOptionChoice {
  label: string;
  extraCost: number;
}

interface ProductOption {
  name: string;
  label: string;
  type: string;
  choices?: ProductOptionChoice[];
  min?: number | null;
  max?: number | null;
  step?: number | null;
  unit?: string | null;
}

interface Product {
  id: string;
  productId: string;
  name: string;
  icon?: string;
  desc?: string;
  basePrice: number;
  options: ProductOption[];
}

interface EstimationItem {
  id: string;
  productName: string;
  productId: string;
  specifications: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  isCustom: boolean;
  width?: number;
  height?: number;
}

interface Estimation {
  estimateNo: string;
  date: string;
  validUntil: string;
  clientInfo: {
    name: string;
    customerId?: string;
    company: string;
    phone: string;
    email: string;
    address: string;
    gstin: string;
  };
  items: EstimationItem[];
  financials: {
    subtotal: number;
    discountType: 'amount' | 'percent';
    discountValue: number;
    discountAmount: number;
    taxRate: number;
    taxAmount: number;
    shippingCharges: number;
    grandTotal: number;
    advanceAmount?: number;
    balanceAmount?: number;
  };
  terms: string;
  privateNotes: string;
  generatedBy?: string;
}

interface EstimationGeneratorProps {
  products: any[];
  users: any[];
  triggerToast?: (msg: string, type: 'success' | 'danger' | 'warning') => void;
}

const defaultTerms = `1. Prices are inclusive of print setup and finishing unless specified.
2. 50% Advance payment required along with confirmed order, balance on delivery.
3. Deliveries will be scheduled within 3-5 working days from artwork approval.
4. Estimate is valid for 30 days from the date of issue.
5. All disputes are subject to Chennai Jurisdiction.`;

const numberToWords = (num: number): string => {
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';

  const convertLessThanOneThousand = (n: number): string => {
    if (n === 0) return '';
    let str = '';
    if (n >= 100) {
      str += a[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      str += b[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) {
      str += a[n] + ' ';
    }
    return str.trim();
  };

  let str = '';
  let temp = Math.floor(num);

  if (Math.floor(temp / 10000000) > 0) {
    str += convertLessThanOneThousand(Math.floor(temp / 10000000)) + ' Crore ';
    temp %= 10000000;
  }
  if (Math.floor(temp / 100000) > 0) {
    str += convertLessThanOneThousand(Math.floor(temp / 100000)) + ' Lakh ';
    temp %= 100000;
  }
  if (Math.floor(temp / 1000) > 0) {
    str += convertLessThanOneThousand(Math.floor(temp / 1000)) + ' Thousand ';
    temp %= 1000;
  }
  if (temp > 0) {
    str += convertLessThanOneThousand(temp);
  }

  return str.trim() + ' Rupees Only';
};

const getItemDimensions = (item: EstimationItem) => {
  let width = item.width;
  let height = item.height;

  // Fallback: parse from specifications if not directly set
  if ((!width || !height) && item.specifications) {
    const match = item.specifications.match(/Size:\s*([\d.]+)\s*(?:ft|')?\s*x\s*([\d.]+)\s*(?:ft|')?/i);
    if (match) {
      width = parseFloat(match[1]);
      height = parseFloat(match[2]);
    }
  }

  const isBanner = Boolean(
    (width && height) ||
    item.productId === 'flex-banners' ||
    (item.productName && item.productName.toLowerCase().includes('banner'))
  );

  if (isBanner && width && height) {
    const area = Number((width * height).toFixed(2));
    const areaFormatted = Number.isInteger(area) ? String(area) : area.toFixed(2);
    return {
      hasSize: true,
      width,
      height,
      area,
      areaFormatted,
      pdfText: `W: ${width} ft × H: ${height} ft\nArea: ${areaFormatted} sq.ft`,
      excelText: `W: ${width} ft × H: ${height} ft (Area: ${areaFormatted} sq.ft)`,
      uiText: `${width} ft × ${height} ft`,
    };
  }

  return {
    hasSize: false,
    width: undefined,
    height: undefined,
    area: undefined,
    areaFormatted: undefined,
    pdfText: '-',
    excelText: '-',
    uiText: '-',
  };
};

const EstimationGenerator: React.FC<EstimationGeneratorProps> = ({ products, users, triggerToast }) => {
  const [activeSubTab, setActiveSubTab] = useState<'create' | 'history'>('create');
  const [estimations, setEstimations] = useState<Estimation[]>([]);
  const [editingEstimateNo, setEditingEstimateNo] = useState<string | null>(null);

  const { data: estimationsData, refetch: refetchEstimations, loading: loadingEstimations } = useQuery(GET_ESTIMATIONS, {
    fetchPolicy: 'network-only'
  });
  const [saveEstimation] = useMutation(SAVE_ESTIMATION);
  const [deleteEstimation] = useMutation(DELETE_ESTIMATION);

  useEffect(() => {
    if (estimationsData?.estimations) {
      setEstimations(estimationsData.estimations);
    }
  }, [estimationsData]);

  // Generator Identity state
  const [generatedBy, setGeneratedBy] = useState(() => localStorage.getItem('username') || 'Admin');

  // Compile list of users whose role is not customer
  const nonCustomerUsers = (users || []).filter((u: any) => u.role !== 'customer');
  const userOptionsSet = new Set<string>();
  userOptionsSet.add('Admin');
  const currentUser = localStorage.getItem('username');
  if (currentUser) {
    userOptionsSet.add(currentUser);
  }
  nonCustomerUsers.forEach((u: any) => {
    if (u.username) userOptionsSet.add(u.username);
  });
  const userOptions = Array.from(userOptionsSet);

  // Client Details state
  const [clientName, setClientName] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientGstin, setClientGstin] = useState('');
  const [clientCustomerId, setClientCustomerId] = useState('');

  const [selectedCustomerId, setSelectedCustomerIdState] = useState<string>('');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const { data: usersResult } = useQuery(GET_ADMIN_USERS, {
    fetchPolicy: 'network-only',
  });
  const customers = React.useMemo(() =>
    (usersResult?.users || []).filter((u: any) => u.role === 'customer'),
    [usersResult]
  );

  // Estimate Meta state
  const [estimateNo, setEstimateNo] = useState('');
  const [estimateDate, setEstimateDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });

  // Line Items state
  const [addedItems, setAddedItems] = useState<EstimationItem[]>([]);

  // Add Item form state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [bannerWidth, setBannerWidth] = useState(3);
  const [bannerHeight, setBannerHeight] = useState(2);
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [isPriceOverridden, setIsPriceOverridden] = useState(false);

  // Custom Item state
  const [customTitle, setCustomTitle] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customUnit, setCustomUnit] = useState('pcs');

  // Summary financials
  const [discountType, setDiscountType] = useState<'amount' | 'percent'>('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [taxRate, setTaxRate] = useState(18); // Default GST 18%
  const [shippingCharges, setShippingCharges] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);

  // Extra notes & terms
  const [terms, setTerms] = useState(defaultTerms);
  const [privateNotes, setPrivateNotes] = useState('');

  // Toast / Status messages (we now prefer the global triggerToast if provided, else fallback to local)
  const [statusMsg, setStatusMsg] = useState<{ text: string; type: 'success' | 'danger' } | null>(null);

  const showStatus = (msg: string, type: 'success' | 'danger' | 'warning') => {
    if (triggerToast) {
      triggerToast(msg, type);
    } else {
      setStatusMsg({ text: msg, type: type === 'warning' ? 'danger' : type });
      setTimeout(() => setStatusMsg(null), 4000);
    }
  };

  // Generate unique estimate number
  useEffect(() => {
    regenerateEstimateNo();
  }, []);

  const regenerateEstimateNo = () => {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const rand = Math.floor(1000 + Math.random() * 9000);
    setEstimateNo(`EST-${today}-${rand}`);
  };

  // Handle standard product selection
  const selectedProduct = products.find(p => p.productId === selectedProductId || p.id === selectedProductId);

  // Set default product options & quantity when selected product changes
  useEffect(() => {
    if (!selectedProduct) {
      setUnitPrice(0);
      setIsPriceOverridden(false);
      return;
    }

    const defaultOptions: Record<string, string> = {};
    selectedProduct.options.forEach(opt => {
      if ((opt.type === 'select' || opt.type === 'radio') && opt.choices && opt.choices.length > 0) {
        defaultOptions[opt.name] = opt.choices[0].label;
      }
    });
    setSelectedOptions(defaultOptions);

    // Default reasonable quantities
    if (selectedProduct.productId === 'visiting-cards') setQuantity(100);
    else if (selectedProduct.productId === 'flex-banners') setQuantity(1);
    else if (selectedProduct.productId === 'flyers' || selectedProduct.productId === 'brochures') setQuantity(250);
    else if (selectedProduct.productId === 'stickers-labels') setQuantity(100);
    else if (selectedProduct.productId === 'wedding-invites') setQuantity(100);
    else if (selectedProduct.productId === 'posters') setQuantity(5);
    else if (selectedProduct.productId === 'id-cards') setQuantity(5);
    else if (selectedProduct.productId === 'certificates') setQuantity(20);
    else if (selectedProduct.productId === 'photo-frames') setQuantity(1);
    else setQuantity(1);

    setIsPriceOverridden(false);
  }, [selectedProductId, selectedProduct]);

  // Calculate standard unit price dynamically based on product choices
  useEffect(() => {
    if (!selectedProduct || isPriceOverridden) return;

    let base = selectedProduct.basePrice;
    selectedProduct.options.forEach(opt => {
      const val = selectedOptions[opt.name];
      if (opt.type === 'select' || opt.type === 'radio') {
        const choice = opt.choices?.find(c => c.label === val);
        if (choice) {
          base += choice.extraCost;
        }
      }
    });

    setUnitPrice(Number(base.toFixed(2)));
  }, [selectedProduct, selectedOptions, isPriceOverridden]);

  // Financial calculations
  const subtotal = addedItems.reduce((acc, item) => acc + item.totalPrice, 0);

  let discountAmount = 0;
  if (discountType === 'percent') {
    discountAmount = Math.round(subtotal * (discountValue / 100));
  } else {
    discountAmount = Number(discountValue) || 0;
  }
  if (discountAmount > subtotal) discountAmount = subtotal;

  const taxableAmount = Math.max(0, subtotal - discountAmount);
  const taxAmount = Math.round(taxableAmount * (taxRate / 100));
  const grandTotal = Math.round(taxableAmount + taxAmount + (Number(shippingCharges) || 0));

  const handleOptionChange = (name: string, val: string) => {
    setSelectedOptions(prev => ({ ...prev, [name]: val }));
    setIsPriceOverridden(false);
  };

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProductId) return;

    let itemTitle = '';
    let specsString = '';
    let totalPrice = 0;
    let isCustom = false;

    if (selectedProductId === 'custom') {
      if (!customTitle.trim()) {
        alert('Please enter a description for custom item.');
        return;
      }
      itemTitle = customTitle.trim();
      specsString = customDesc.trim() || 'Custom Order';
      totalPrice = Math.round(unitPrice * quantity);
      isCustom = true;
    } else if (selectedProduct) {
      itemTitle = selectedProduct.name;
      isCustom = false;

      // Compile specs string
      const specsList: string[] = [];
      selectedProduct.options.forEach(opt => {
        if (opt.type === 'select' || opt.type === 'radio') {
          const val = selectedOptions[opt.name];
          if (val) specsList.push(`${opt.label}: ${val}`);
        }
      });

      if (selectedProduct.productId === 'flex-banners') {
        specsList.push(`Size: ${bannerWidth}ft x ${bannerHeight}ft`);
        specsString = specsList.join(', ');
        const area = bannerWidth * bannerHeight;
        totalPrice = Math.round(unitPrice * area * quantity);
      } else {
        specsString = specsList.join(', ');
        totalPrice = Math.round(unitPrice * quantity);
      }
    }

    const newItem: EstimationItem = {
      id: Math.random().toString(36).substring(2, 9),
      productName: itemTitle,
      productId: isCustom ? '' : selectedProductId,
      specifications: specsString,
      quantity,
      unitPrice,
      totalPrice,
      isCustom,
      width: selectedProductId === 'flex-banners' ? bannerWidth : undefined,
      height: selectedProductId === 'flex-banners' ? bannerHeight : undefined,
    };

    setAddedItems(prev => [...prev, newItem]);

    // Reset add state
    setCustomTitle('');
    setCustomDesc('');
    setSelectedProductId('');
    setUnitPrice(0);
    setQuantity(1);
    setIsPriceOverridden(false);
  };

  const handleDeleteItem = (id: string) => {
    setAddedItems(prev => prev.filter(i => i.id !== id));
  };

  const handleEditItem = (item: EstimationItem) => {
    if (item.isCustom) {
      setSelectedProductId('custom');
      setCustomTitle(item.productName);
      setCustomDesc(item.specifications);
    } else {
      setSelectedProductId(item.productId || '');
      if (item.width) setBannerWidth(item.width);
      if (item.height) setBannerHeight(item.height);
      if (item.specifications) {
        const parsedOpts: Record<string, string> = {};
        const pairs = item.specifications.split(', ');
        pairs.forEach(pair => {
          const colonIdx = pair.indexOf(': ');
          if (colonIdx > -1) {
            const optLabel = pair.substring(0, colonIdx).trim();
            const optVal = pair.substring(colonIdx + 2).trim();
            const prod = products.find(p => p.productId === item.productId || p.id === item.productId);
            if (prod) {
              const matchedOpt = prod.options.find((o: any) => o.label === optLabel || o.name === optLabel);
              if (matchedOpt) {
                parsedOpts[matchedOpt.name] = optVal;
              }
            }
          }
        });
        if (Object.keys(parsedOpts).length > 0) {
          setSelectedOptions(prev => ({ ...prev, ...parsedOpts }));
        }
      }
    }
    setQuantity(item.quantity);
    setUnitPrice(item.unitPrice);
    setIsPriceOverridden(true);
    handleDeleteItem(item.id);
    showStatus(`Loaded "${item.productName}" for editing. Click "Add" when ready.`, 'warning');
  };

  const handleEditEstimation = (est: Estimation) => {
    setEditingEstimateNo(est.estimateNo);
    setEstimateNo(est.estimateNo);
    setEstimateDate(est.date || new Date().toISOString().slice(0, 10));
    setValidUntil(est.validUntil || (() => {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      return d.toISOString().slice(0, 10);
    })());
    setGeneratedBy(est.generatedBy || localStorage.getItem('username') || 'Admin');

    // Client information
    setClientName(est.clientInfo?.name || '');
    setClientCompany(est.clientInfo?.company || '');
    setClientPhone(est.clientInfo?.phone || '');
    setClientEmail(est.clientInfo?.email || '');
    setClientAddress(est.clientInfo?.address || '');
    setClientGstin(est.clientInfo?.gstin || '');
    setClientCustomerId(est.clientInfo?.customerId || '');

    // Match existing profile customer if possible
    const matchedCustomer = customers.find((c: any) =>
      (est.clientInfo?.customerId && (c.customerId === est.clientInfo.customerId || c.id === est.clientInfo.customerId)) ||
      (c.username && c.username.toLowerCase() === (est.clientInfo?.name || '').toLowerCase())
    );
    if (matchedCustomer) {
      setSelectedCustomerIdState(matchedCustomer.id);
    } else {
      setSelectedCustomerIdState('');
    }

    // Line items
    const parsedItems: EstimationItem[] = (est.items || []).map((item: any, idx: number) => ({
      id: item.id || `item-${Date.now()}-${idx}`,
      productName: item.productName || 'Item',
      productId: item.productId || '',
      specifications: item.specifications || '',
      quantity: Number(item.quantity) || 1,
      unitPrice: Number(item.unitPrice) || 0,
      totalPrice: Number(item.totalPrice) || (Number(item.unitPrice || 0) * Number(item.quantity || 1)),
      isCustom: Boolean(item.isCustom),
      width: item.width ? Number(item.width) : undefined,
      height: item.height ? Number(item.height) : undefined,
    }));
    setAddedItems(parsedItems);

    // Financials
    setDiscountType((est.financials?.discountType as 'amount' | 'percent') || 'percent');
    setDiscountValue(est.financials?.discountValue || 0);
    setTaxRate(est.financials?.taxRate ?? 18);
    setShippingCharges(est.financials?.shippingCharges || 0);
    setAdvanceAmount(est.financials?.advanceAmount || 0);

    // Terms & notes
    setTerms(est.terms || defaultTerms);
    setPrivateNotes(est.privateNotes || '');

    // Reset item add form
    setSelectedProductId('');
    setCustomTitle('');
    setCustomDesc('');
    setUnitPrice(0);
    setQuantity(1);
    setIsPriceOverridden(false);

    // Switch to create tab
    setActiveSubTab('create');
    showStatus(`Editing Estimate #${est.estimateNo}`, 'success');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveEstimation = async () => {
    if (!clientName.trim()) {
      showStatus('Customer name is required.', 'danger');
      return;
    }
    if (addedItems.length === 0) {
      showStatus('Please add at least one item to generate estimate.', 'danger');
      return;
    }

    const newEstimation = {
      estimateNo,
      date: estimateDate,
      validUntil,
      clientInfo: {
        name: clientName,
        customerId: clientCustomerId || undefined,
        company: clientCompany || undefined,
        phone: clientPhone,
        email: clientEmail || undefined,
        address: clientAddress || undefined,
        gstin: clientGstin || undefined,
      },
      items: addedItems.map(item => ({
        id: item.id,
        productName: item.productName,
        productId: item.productId || undefined,
        specifications: item.specifications || undefined,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        isCustom: item.isCustom,
        width: item.width || undefined,
        height: item.height || undefined,
      })),
      financials: {
        subtotal,
        discountType,
        discountValue,
        discountAmount,
        taxRate,
        taxAmount,
        shippingCharges: Number(shippingCharges) || 0,
        grandTotal,
        advanceAmount: Number(advanceAmount) || 0,
        balanceAmount: grandTotal - (Number(advanceAmount) || 0),
      },
      terms: terms || undefined,
      privateNotes: privateNotes || undefined,
      generatedBy: generatedBy || undefined,
    };

    try {
      const { data } = await saveEstimation({
        variables: { input: newEstimation }
      });
      if (data?.saveEstimation?.success) {
        showStatus(data.saveEstimation.message || `Estimation ${estimateNo} saved successfully!`, 'success');
        refetchEstimations();
      } else {
        showStatus(data?.saveEstimation?.message || 'Failed to save estimation.', 'danger');
      }
    } catch (err: any) {
      console.error(err);
      showStatus(err.message || 'An error occurred while saving.', 'danger');
    }
  };

  const handleClearForm = () => {
    setEditingEstimateNo(null);
    setSelectedCustomerIdState('');
    setClientName('');
    setClientCompany('');
    setClientPhone('');
    setClientEmail('');
    setClientAddress('');
    setClientGstin('');
    setClientCustomerId('');
    setAddedItems([]);
    setDiscountType('percent');
    setDiscountValue(0);
    setTaxRate(18);
    setShippingCharges(0);
    setAdvanceAmount(0);
    setPrivateNotes('');
    setTerms(defaultTerms);
    setGeneratedBy(localStorage.getItem('username') || 'Admin');
    regenerateEstimateNo();
  };

  const handleDeleteHistory = async (no: string) => {
    if (!window.confirm(`Are you sure you want to delete Estimate ${no} from history?`)) return;
    try {
      const { data } = await deleteEstimation({
        variables: { estimateNo: no }
      });
      if (data?.deleteEstimation?.success) {
        showStatus(`Estimate ${no} deleted successfully.`, 'success');
        refetchEstimations();
        if (editingEstimateNo === no) {
          handleClearForm();
        }
      } else {
        showStatus(data?.deleteEstimation?.message || 'Failed to delete estimate.', 'danger');
      }
    } catch (err: any) {
      console.error('Error deleting estimation:', err);
      setEstimations(prev => prev.filter(e => e.estimateNo !== no));
      showStatus(err.message || 'Error deleting estimate.', 'danger');
    }
  };

  // PDF Export logic
  const handleDownloadPDF = (est: Estimation) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const primaryColor = [21, 101, 192]; // Premium blue
    const secondaryColor = [74, 85, 104];
    const lightBg = [248, 250, 252];

    // Header Panel
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, 210, 42, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('PRATHITHA DIGITAL PRINTERS', 15, 16);

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('No.42, J.V.R Nagar Complex, N.S.R Road, Chromepet, Chennai - 600044', 15, 24);
    doc.text('Phone: +91 91508 08130 | Email: pdp@prathithadp.com', 15, 29);
    doc.text('GSTIN: 33CHVPP2054B1ZE', 15, 34);

    doc.setFontSize(16);
    doc.setFont('Helvetica', 'bold');
    doc.text('ESTIMATE', 195, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');
    doc.text(`Estimate #: ${est.estimateNo}`, 195, 27, { align: 'right' });
    doc.text(`Date: ${est.date}`, 195, 33, { align: 'right' });

    let y = 52;

    // Billing details block
    doc.setFillColor(lightBg[0], lightBg[1], lightBg[2]);
    doc.rect(15, y, 180, 32, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.rect(15, y, 180, 32, 'S');

    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('ESTIMATE FOR:', 20, y + 6);
    doc.text('ESTIMATE DETAILS:', 115, y + 6);

    doc.setTextColor(51, 51, 51);
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(est.clientInfo.name, 20, y + 12);

    doc.setFont('Helvetica', 'normal');
    if (est.clientInfo.customerId) {
      doc.text(`Customer ID: ${est.clientInfo.customerId}`, 20, y + 17);
    }

    let currentY = est.clientInfo.customerId ? y + 22 : y + 17;

    if (est.clientInfo.company) {
      doc.text(est.clientInfo.company, 20, currentY);
      currentY += 5;
    }
    const contactStr = [est.clientInfo.phone, est.clientInfo.email].filter(Boolean).join(' | ');
    doc.text(contactStr, 20, currentY);
    currentY += 5;

    if (est.clientInfo.gstin) {
      doc.text(`GSTIN: ${est.clientInfo.gstin}`, 20, currentY);
    }

    doc.text(`Valid Until: ${est.validUntil}`, 115, y + 12);
    if (est.clientInfo.address) {
      doc.text('Address:', 115, y + 17);
      const splitAddress = doc.splitTextToSize(est.clientInfo.address, 75);
      doc.text(splitAddress, 115, y + 22);
    }

    y += 39;

    // Items table
    const tableColumn = ['S.No', 'Product & Specifications', 'Size (W × H & Area)', 'Qty', 'Unit Price', 'Total'];
    const tableRows = est.items.map((item, index) => {
      const dim = getItemDimensions(item);
      let unitCostStr = `Rs. ${item.unitPrice.toLocaleString('en-IN')}`;
      if (dim.hasSize) {
        unitCostStr += ' /sq.ft';
      }
      return [
        (index + 1).toString(),
        `${item.productName}\n${item.specifications}`,
        dim.pdfText,
        item.quantity.toString(),
        unitCostStr,
        `Rs. ${item.totalPrice.toLocaleString('en-IN')}`,
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: y,
      theme: 'grid',
      headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 8.5, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 68 },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 27, halign: 'right' },
        5: { cellWidth: 26, halign: 'right' },
      },
    });

    y = (doc as any).lastAutoTable.finalY + 8;

    // Financial calculations panel (right side)
    const labelX = 145;
    const valueX = 195;

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);

    doc.text('Subtotal:', labelX, y);
    doc.text(`Rs. ${est.financials.subtotal.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
    y += 6;

    if (est.financials.discountAmount > 0) {
      doc.text(`Discount (${est.financials.discountType === 'percent' ? est.financials.discountValue + '%' : 'Fixed'}):`, labelX, y);
      doc.text(`- Rs. ${est.financials.discountAmount.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
      y += 6;
    }

    if (est.financials.taxAmount > 0) {
      doc.text(`GST (${est.financials.taxRate}%):`, labelX, y);
      doc.text(`Rs. ${est.financials.taxAmount.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
      y += 6;
    }

    if (est.financials.shippingCharges > 0) {
      doc.text('Shipping & Delivery:', labelX, y);
      doc.text(`Rs. ${est.financials.shippingCharges.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
      y += 6;
    }

    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Grand Total:', labelX, y);
    doc.text(`Rs. ${est.financials.grandTotal.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
    y += 6;

    if (est.financials.advanceAmount && est.financials.advanceAmount > 0) {
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.text('Advance:', labelX, y);
      doc.text(`- Rs. ${est.financials.advanceAmount.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
      y += 6;

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text('Balance Due:', labelX, y);
      const bal = est.financials.balanceAmount ?? (est.financials.grandTotal - est.financials.advanceAmount);
      doc.text(`Rs. ${bal.toLocaleString('en-IN')}`, valueX, y, { align: 'right' });
      y += 6;
    }
    y += 2;

    // Amount in Words
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Amount in Words:', 15, y - 24 > 90 ? y - 24 : y);
    doc.setFont('Helvetica', 'italic');
    doc.setTextColor(51, 51, 51);
    doc.text(numberToWords(est.financials.grandTotal), 48, y - 24 > 90 ? y - 24 : y);

    if (y - 24 <= 90) {
      y += 10;
    } else {
      y += 2;
    }

    // Terms and Conditions
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Terms & Conditions:', 15, y);

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    const splitTerms = doc.splitTextToSize(est.terms, 180);
    doc.text(splitTerms, 15, y + 5);

    y += 40;

    // Signatures
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.line(15, y + 10, 75, y + 10);
    doc.text(`Prepared By (Prathitha) - ${est.generatedBy || 'Admin'}`, 15, y + 14);

    doc.line(135, y + 10, 195, y + 10);
    doc.text('Customer Acceptance Signature', 135, y + 14);

    doc.setFontSize(7.5);
    doc.text('This is an electronically generated estimation copy, no physical signature required unless accepted.', 15, 282);

    doc.save(`Estimate_${est.clientInfo.name.replace(/\s+/g, '_')}_${est.estimateNo}.pdf`);
  };

  // Excel Export logic
  const handleExportExcel = (est: Estimation) => {
    // Generate rows array
    const rows = [
      ['PRATHITHA DIGITAL PRINTERS', '', '', '', ''],
      ['No.42, J.V.R Nagar Complex, N.S.R Road, Chromepet, Chennai - 600044', '', '', '', ''],
      ['Phone: +91 91508 08130 | Email: contact@prathitha.com', '', '', '', ''],
      ['GSTIN: 33AAAAA1111A1Z1', '', '', '', ''],
      ['', '', '', '', ''],
      ['ESTIMATE / QUOTATION', '', '', '', ''],
      [`Estimate No:`, est.estimateNo, '', `Date:`, est.date],
      [`Valid Until:`, est.validUntil, '', `Prepared By:`, est.generatedBy || 'Admin'],
      ['', '', '', '', ''],
      ['CUSTOMER DETAILS', '', '', '', ''],
      ['Name:', est.clientInfo.name, '', 'Company:', est.clientInfo.company || '-'],
      ['Customer ID:', est.clientInfo.customerId || '-', '', 'Phone:', est.clientInfo.phone || '-'],
      ['Email:', est.clientInfo.email || '-', '', 'GSTIN:', est.clientInfo.gstin || '-'],
      ['Address:', est.clientInfo.address || '-', '', '', ''],
      ['', '', '', '', ''],
      ['LINE ITEMS', '', '', '', '', ''],
      ['S.No', 'Product & Specifications', 'Size (Width × Height & Area)', 'Quantity', 'Unit Price (₹)', 'Total (₹)'],
    ];

    est.items.forEach((item, index) => {
      const dim = getItemDimensions(item);
      let unitPriceStr = String(item.unitPrice);
      if (dim.hasSize) {
        unitPriceStr += ' /sq.ft';
      }
      rows.push([
        String(index + 1),
        `${item.productName} — ${item.specifications}`,
        dim.excelText,
        String(item.quantity),
        unitPriceStr,
        String(item.totalPrice)
      ]);
    });

    rows.push(['', '', '', '', '', '']);
    rows.push(['', '', '', '', 'Subtotal:', String(est.financials.subtotal)]);

    if (est.financials.discountAmount > 0) {
      const typeStr = est.financials.discountType === 'percent' ? `Discount (${est.financials.discountValue}%):` : 'Discount:';
      rows.push(['', '', '', '', typeStr, `-${est.financials.discountAmount}`]);
    }

    if (est.financials.taxAmount > 0) {
      rows.push(['', '', '', '', `GST (${est.financials.taxRate}%):`, String(est.financials.taxAmount)]);
    }

    if (est.financials.shippingCharges > 0) {
      rows.push(['', '', '', '', 'Shipping Charges:', String(est.financials.shippingCharges)]);
    }

    rows.push(['', '', '', '', 'Grand Total:', String(est.financials.grandTotal)]);

    if (est.financials.advanceAmount && est.financials.advanceAmount > 0) {
      rows.push(['', '', '', '', 'Advance:', `-${est.financials.advanceAmount}`]);
      const bal = est.financials.balanceAmount ?? (est.financials.grandTotal - est.financials.advanceAmount);
      rows.push(['', '', '', '', 'Balance Due:', String(bal)]);
    }
    rows.push(['', '', '', '', '', '']);
    rows.push(['Amount in Words:', numberToWords(est.financials.grandTotal), '', '', '', '']);
    rows.push(['', '', '', '', '', '']);

    rows.push(['TERMS & CONDITIONS', '', '', '', '', '']);
    est.terms.split('\n').forEach(line => {
      rows.push([line, '', '', '', '', '']);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Estimation Sheet');

    // Auto-fit column widths
    const maxLens = [10, 42, 34, 12, 18, 16];
    worksheet['!cols'] = maxLens.map(w => ({ wch: w }));

    XLSX.writeFile(workbook, `Estimate_${est.clientInfo.name.replace(/\s+/g, '_')}_${est.estimateNo}.xlsx`);
  };

  return (
    <Card className="border-0 shadow-sm rounded-4 p-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 border-bottom pb-3">
        <div>
          <h4 className="fw-bold text-dark mb-1">Print Price Estimation Generator</h4>
          <p className="text-muted mb-0" style={{ fontSize: '12.5px' }}>Configure products, specifications, quantities and instantly export quotes in Excel & PDF formats.</p>
        </div>
        <div className="btn-group border rounded-3 p-0.5 bg-light" style={{ height: '36px' }}>
          <Button
            variant={activeSubTab === 'create' && !editingEstimateNo ? 'white' : 'light'}
            size="sm"
            onClick={() => {
              if (editingEstimateNo) {
                handleClearForm();
              }
              setActiveSubTab('create');
            }}
            className={`px-3 py-1 border-0 fw-semibold ${activeSubTab === 'create' && !editingEstimateNo ? 'shadow-sm text-primary rounded-2 bg-white' : 'text-muted bg-transparent'}`}
            style={{ fontSize: '12.5px' }}
          >
            <i className="fas fa-plus-circle me-1"></i> New Estimate
          </Button>
          {editingEstimateNo && (
            <Button
              variant={activeSubTab === 'create' ? 'white' : 'light'}
              size="sm"
              onClick={() => setActiveSubTab('create')}
              className={`px-3 py-1 border-0 fw-semibold ${activeSubTab === 'create' ? 'shadow-sm text-warning rounded-2 bg-white' : 'text-muted bg-transparent'}`}
              style={{ fontSize: '12.5px' }}
            >
              <i className="fas fa-edit me-1 text-warning"></i> Editing ({editingEstimateNo})
            </Button>
          )}
          <Button
            variant={activeSubTab === 'history' ? 'white' : 'light'}
            size="sm"
            onClick={() => setActiveSubTab('history')}
            className={`px-3 py-1 border-0 fw-semibold ${activeSubTab === 'history' ? 'shadow-sm text-primary rounded-2 bg-white' : 'text-muted bg-transparent'}`}
            style={{ fontSize: '12.5px' }}
          >
            <i className="fas fa-history me-1"></i> History ({estimations.length})
          </Button>
        </div>
      </div>

      {statusMsg && (
        <Alert variant={statusMsg.type === 'success' ? 'success' : 'danger'} className="border-0 rounded-3 mb-4" onClose={() => setStatusMsg(null)} dismissible>
          {statusMsg.text}
        </Alert>
      )}

      {activeSubTab === 'create' ? (
        <div>
          {editingEstimateNo && (
            <Alert variant="warning" className="d-flex justify-content-between align-items-center rounded-3 mb-4 py-2.5 px-3 border border-warning shadow-sm flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge bg="warning" text="dark" className="px-2.5 py-1.5 fs-7">
                  <i className="fas fa-edit me-1"></i> Edit Mode
                </Badge>
                <span className="fw-bold text-dark">
                  Editing Estimate: <span className="font-monospace text-primary">{editingEstimateNo}</span>
                </span>
                {clientName && (
                  <span className="text-muted" style={{ fontSize: '13px' }}>
                    • Customer: <strong>{clientName}</strong>
                  </span>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  style={{ borderRadius: '6px', fontSize: '12px' }}
                  onClick={() => setActiveSubTab('history')}
                >
                  <i className="fas fa-arrow-left me-1"></i> Back to History
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ borderRadius: '6px', fontSize: '12px' }}
                  onClick={handleClearForm}
                >
                  <i className="fas fa-times me-1"></i> Cancel & Create New
                </Button>
              </div>
            </Alert>
          )}

          {/* CUSTOMER & QUOTE CONFIG SECTION */}
          <Card className="border-0 bg-light p-3 rounded-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold text-primary mb-0" style={{ fontSize: '14px' }}>
                <i className="far fa-address-card me-2"></i>Customer & Quotation Meta
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted fw-semibold" style={{ fontSize: '12px' }}>Estimate No:</span>
                <span className={`badge ${editingEstimateNo ? 'bg-warning text-dark border-warning' : 'bg-white text-primary border'} font-monospace px-2.5 py-1.5 fs-6 shadow-sm`}>
                  {estimateNo}
                </span>
                {editingEstimateNo ? (
                  <Badge bg="primary" className="px-2 py-1">Editing</Badge>
                ) : (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-muted"
                    title="Regenerate Estimate Number"
                    onClick={regenerateEstimateNo}
                  >
                    <i className="fas fa-sync-alt" style={{ fontSize: '12px' }}></i>
                  </Button>
                )}
              </div>
            </div>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '11px' }}>
                    <span>Select Profile Customer</span>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none fw-semibold"
                      style={{ fontSize: '11px' }}
                      onClick={() => setShowAddCustomerModal(true)}
                    >
                      <i className="fas fa-plus me-1"></i>New Profile Customer
                    </Button>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedCustomerId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedCustomerIdState(val);
                      const selected = customers.find((c: any) => c.id === val);
                      if (selected) {
                        setClientName(selected.username || '');
                        setClientCustomerId(selected.customerId || '');
                        setClientCompany(selected.companyName || '');
                        setClientPhone(selected.phone || '');
                        setClientEmail(selected.email || '');
                        setClientAddress(selected.companyAddress || selected.shippingAddress || '');
                        setClientGstin(selected.gstNumber || '');
                      } else {
                        setClientName('');
                        setClientCustomerId('');
                        setClientCompany('');
                        setClientPhone('');
                        setClientEmail('');
                        setClientAddress('');
                        setClientGstin('');
                      }
                    }}
                    style={{ borderRadius: '8px' }}
                  >
                    <option value="">-- Select Existing Customer (or Add New) --</option>
                    {customers.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.username} {c.companyName ? `(${c.companyName})` : ''} - {c.phone || 'No phone'}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Customer Name *</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. Ramesh Kumar"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    style={{ borderRadius: '8px' }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Company Name (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. RK Tech Solutions"
                    value={clientCompany}
                    onChange={(e) => setClientCompany(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. 9876543210"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    size="sm"
                    placeholder="e.g. customer@gmail.com"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Billing Address</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. 12/4, Station Road, Chromepet, Chennai - 44"
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Customer GSTIN</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. 33AAAAA1111A1Z1"
                    value={clientGstin}
                    onChange={(e) => setClientGstin(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Prepared By</Form.Label>
                  <Form.Select
                    size="sm"
                    value={generatedBy}
                    onChange={(e) => setGeneratedBy(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  >
                    {userOptions.map((optName) => (
                      <option key={optName} value={optName}>
                        {optName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Estimate Date</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={estimateDate}
                    onChange={(e) => setEstimateDate(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Valid Until Date</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card>

          {/* ADD ITEM SECTION */}
          <Card className="border shadow-sm p-4 rounded-4 mb-4">
            <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '14px' }}>
              <i className="fas fa-cart-plus text-primary me-2"></i>Add Line Item to Quotation
            </h5>
            <Form onSubmit={handleAddItem}>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Select Product Catalogue *</Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      style={{ borderRadius: '8px' }}
                      required
                    >
                      <option value="">-- Select Product --</option>
                      <option value="custom" className="fw-bold text-primary">+ Custom Line Item</option>
                      {products.map(p => (
                        <option key={p.id} value={p.productId}>{p.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* DYNAMIC PRODUCT OPTIONS */}
                {selectedProduct && (
                  <>
                    {selectedProduct.options.map(opt => {
                      if (opt.type === 'select' || opt.type === 'radio') {
                        return (
                          <Col md={3} key={opt.name}>
                            <Form.Group>
                              <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>{opt.label}</Form.Label>
                              <Form.Select
                                size="sm"
                                value={selectedOptions[opt.name] || ''}
                                onChange={(e) => handleOptionChange(opt.name, e.target.value)}
                                style={{ borderRadius: '8px' }}
                              >
                                {opt.choices?.map(c => (
                                  <option key={c.label} value={c.label}>
                                    {c.label} {c.extraCost > 0 ? `(+₹${c.extraCost})` : ''}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        );
                      }
                      return null;
                    })}

                    {/* WIDTH & HEIGHT FOR BANNER */}
                    {selectedProduct.productId === 'flex-banners' && (
                      <>
                        <Col md={1.5} xs={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Width (ft)</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              min={1}
                              value={bannerWidth}
                              onChange={(e) => {
                                setBannerWidth(Number(e.target.value) || 1);
                                setIsPriceOverridden(false);
                              }}
                              style={{ borderRadius: '8px' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={1.5} xs={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Height (ft)</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              min={1}
                              value={bannerHeight}
                              onChange={(e) => {
                                setBannerHeight(Number(e.target.value) || 1);
                                setIsPriceOverridden(false);
                              }}
                              style={{ borderRadius: '8px' }}
                            />
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </>
                )}

                {/* CUSTOM LINE ITEM FIELDS */}
                {selectedProductId === 'custom' && (
                  <>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Item Title *</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. Custom Cardboard Standee"
                          value={customTitle}
                          onChange={(e) => setCustomTitle(e.target.value)}
                          style={{ borderRadius: '8px' }}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Specifications / Description</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. 5mm Sunboard + Glossy Lamination + Back Support"
                          value={customDesc}
                          onChange={(e) => setCustomDesc(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1.5} xs={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Unit Label</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="pcs"
                          value={customUnit}
                          onChange={(e) => setCustomUnit(e.target.value)}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}

                {selectedProductId && (
                  <>
                    <Col md={1.5} xs={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>Quantity</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={1}
                          value={quantity}
                          onChange={(e) => setQuantity(Number(e.target.value) || 1)}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} xs={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>
                          Unit Price (₹) {isPriceOverridden && <Badge bg="warning" text="dark">Overridden</Badge>}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={0}
                          step="0.01"
                          value={unitPrice}
                          onChange={(e) => {
                            setUnitPrice(Number(e.target.value) || 0);
                            setIsPriceOverridden(true);
                          }}
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        className="w-100 fw-semibold"
                        style={{ height: '36px', borderRadius: '8px' }}
                      >
                        Add
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </Form>
          </Card>

          {/* ADDED ITEMS LIST */}
          <h5 className="fw-bold text-dark mb-3 mt-4" style={{ fontSize: '14.5px' }}>
            Added Items Summary ({addedItems.length})
          </h5>
          {addedItems.length === 0 ? (
            <Alert variant="info" className="text-center py-4 border-dashed border-2 rounded-4">
              <i className="fas fa-calculator mb-2" style={{ fontSize: '20px' }}></i>
              <div>No items configured yet. Configure options and click "Add" to start building your quote!</div>
            </Alert>
          ) : (
            <div className="table-responsive mb-4">
              <Table hover className="align-middle border mb-0 rounded-3 overflow-hidden">
                <thead className="table-light">
                  <tr style={{ fontSize: '11.5px', color: '#64748b' }}>
                    <th className="py-2.5 text-center" style={{ width: '50px' }}>S.No</th>
                    <th>Product & Specifications</th>
                    <th className="text-center" style={{ width: '150px' }}>Size (W × H & Area)</th>
                    <th className="text-center" style={{ width: '90px' }}>Quantity</th>
                    <th className="text-end" style={{ width: '130px' }}>Unit Price (₹)</th>
                    <th className="text-end" style={{ width: '130px' }}>Total Price (₹)</th>
                    <th className="text-center" style={{ width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {addedItems.map((item, idx) => {
                    const dim = getItemDimensions(item);
                    return (
                      <tr key={item.id} style={{ fontSize: '13px' }}>
                        <td className="text-center fw-semibold text-muted">{idx + 1}</td>
                        <td>
                          <span className="fw-semibold text-dark">{item.productName}</span>
                          {item.isCustom && <Badge bg="secondary" className="ms-2">Custom</Badge>}
                          <div className="text-muted mt-0.5" style={{ fontSize: '11.5px' }}>{item.specifications}</div>
                        </td>
                        <td className="text-center">
                          {dim.hasSize ? (
                            <div>
                              <span className="fw-semibold text-dark d-block">
                                {dim.width} ft × {dim.height} ft
                              </span>
                              <Badge bg="light" text="dark" className="border font-monospace mt-0.5" style={{ fontSize: '11px' }}>
                                Area: {dim.areaFormatted} sq.ft
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center fw-bold">{item.quantity}</td>
                        <td className="text-end font-monospace">
                          ₹{item.unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          {dim.hasSize && <div className="text-muted" style={{ fontSize: '10px' }}>per sq.ft</div>}
                        </td>
                        <td className="text-end font-monospace fw-bold text-primary">₹{item.totalPrice.toLocaleString('en-IN')}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-0.5 px-1.5"
                            style={{ borderRadius: '5px' }}
                            onClick={() => handleEditItem(item)}
                            title="Edit this item"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-0.5 px-1.5"
                            style={{ borderRadius: '5px' }}
                            onClick={() => handleDeleteItem(item.id)}
                            title="Delete this item"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* LOWER SECTION: TERMS, NOTES & FINANCIAL PANEL */}
          <Row className="g-4">
            <Col lg={7}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11.5px' }}>Terms & Conditions (Visible on PDF/Excel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  style={{ borderRadius: '10px', fontSize: '12px' }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-muted mb-1" style={{ fontSize: '11.5px' }}>Internal/Private Notes (Not shown on exported files)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Notes about negotiation, follow-up date, custom discounts..."
                  value={privateNotes}
                  onChange={(e) => setPrivateNotes(e.target.value)}
                  style={{ borderRadius: '10px', fontSize: '12px' }}
                />
              </Form.Group>
            </Col>

            <Col lg={5}>
              <Card className="border-0 shadow-sm bg-light p-4 rounded-4">
                <h5 className="fw-bold text-dark border-bottom pb-2 mb-3" style={{ fontSize: '14px' }}>
                  Estimate Summary
                </h5>
                <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: '13px' }}>
                  <span className="text-muted">Subtotal:</span>
                  <span className="font-monospace fw-bold">₹{subtotal.toLocaleString('en-IN')}</span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted" style={{ fontSize: '13px' }}>Discount:</span>
                  <div className="d-flex gap-2 align-items-center" style={{ width: '180px' }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={0}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(Number(e.target.value) || 0)}
                      style={{ borderRadius: '6px' }}
                    />
                    <Form.Select
                      size="sm"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value as 'amount' | 'percent')}
                      style={{ borderRadius: '6px', width: '80px' }}
                    >
                      <option value="percent">%</option>
                      <option value="amount">₹</option>
                    </Form.Select>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted" style={{ fontSize: '13px' }}>Tax (GST):</span>
                  <div className="d-flex gap-2 align-items-center" style={{ width: '180px' }}>
                    <Form.Select
                      size="sm"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
                      style={{ borderRadius: '6px' }}
                    >
                      <option value="0">0% (Nil)</option>
                      <option value="5">5% (GST)</option>
                      <option value="12">12% (GST)</option>
                      <option value="18">18% (GST)</option>
                      <option value="28">28% (GST)</option>
                    </Form.Select>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted" style={{ fontSize: '13px' }}>Shipping & Handling:</span>
                  <div className="d-flex gap-2 align-items-center" style={{ width: '180px' }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={0}
                      placeholder="0"
                      value={shippingCharges}
                      onChange={(e) => setShippingCharges(Number(e.target.value) || 0)}
                      style={{ borderRadius: '6px' }}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
                  <span className="text-muted" style={{ fontSize: '13px' }}>Advance Amount:</span>
                  <div className="d-flex gap-2 align-items-center" style={{ width: '180px' }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={0}
                      placeholder="0"
                      value={advanceAmount}
                      onChange={(e) => setAdvanceAmount(Number(e.target.value) || 0)}
                      style={{ borderRadius: '6px' }}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold text-dark fs-6">Grand Total:</span>
                  <span className="fw-extrabold text-primary font-monospace fs-4">₹{grandTotal.toLocaleString('en-IN')}</span>
                </div>

                {advanceAmount > 0 && (
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <span className="fw-bold text-dark fs-6">Balance Due:</span>
                    <span className="fw-bold text-danger font-monospace fs-5">₹{(grandTotal - advanceAmount).toLocaleString('en-IN')}</span>
                  </div>
                )}

                <div className={`d-flex flex-column gap-2 ${advanceAmount === 0 ? 'mt-4' : ''}`}>
                  <Button
                    variant={editingEstimateNo ? "warning" : "success"}
                    className="fw-bold w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                    style={{ borderRadius: '10px' }}
                    onClick={handleSaveEstimation}
                    disabled={addedItems.length === 0}
                  >
                    <i className={`fas ${editingEstimateNo ? 'fa-check-circle' : 'fa-save'}`}></i>
                    {editingEstimateNo ? `Update Estimate (${estimateNo})` : 'Save Estimate in History'}
                  </Button>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      className="fw-bold flex-grow-1 py-2 d-flex align-items-center justify-content-center gap-1.5"
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      onClick={() => handleDownloadPDF({
                        estimateNo,
                        date: estimateDate,
                        validUntil,
                        clientInfo: {
                          name: clientName || 'Walk-in Customer',
                          company: clientCompany,
                          phone: clientPhone,
                          email: clientEmail,
                          address: clientAddress,
                          gstin: clientGstin,
                        },
                        items: addedItems,
                        financials: {
                          subtotal,
                          discountType,
                          discountValue,
                          discountAmount,
                          taxRate,
                          taxAmount,
                          shippingCharges,
                          grandTotal,
                          advanceAmount,
                          balanceAmount: grandTotal - advanceAmount,
                        },
                        terms,
                        privateNotes,
                        generatedBy,
                      })}
                      disabled={addedItems.length === 0}
                    >
                      <i className="fas fa-file-pdf text-danger"></i> PDF
                    </Button>
                    <Button
                      variant="outline-success"
                      className="fw-bold flex-grow-1 py-2 d-flex align-items-center justify-content-center gap-1.5"
                      style={{ borderRadius: '8px', fontSize: '13px' }}
                      onClick={() => handleExportExcel({
                        estimateNo,
                        date: estimateDate,
                        validUntil,
                        clientInfo: {
                          name: clientName || 'Walk-in Customer',
                          company: clientCompany,
                          phone: clientPhone,
                          email: clientEmail,
                          address: clientAddress,
                          gstin: clientGstin,
                        },
                        items: addedItems,
                        financials: {
                          subtotal,
                          discountType,
                          discountValue,
                          discountAmount,
                          taxRate,
                          taxAmount,
                          shippingCharges,
                          grandTotal,
                          advanceAmount,
                          balanceAmount: grandTotal - advanceAmount,
                        },
                        terms,
                        privateNotes,
                        generatedBy,
                      })}
                      disabled={addedItems.length === 0}
                    >
                      <i className="fas fa-file-excel text-success"></i> Excel
                    </Button>
                  </div>
                  <Button
                    variant="link"
                    className="text-danger py-1 mt-2 text-decoration-none fw-semibold"
                    onClick={handleClearForm}
                  >
                    <i className="fas fa-trash me-1"></i> {editingEstimateNo ? 'Cancel Edit & Reset Form' : 'Reset / Clear Form'}
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ) : (
        /* ESTIMATION HISTORY TAB */
        <div>
          {estimations.length === 0 ? (
            <Alert variant="info" className="text-center py-5 border-dashed border-2 rounded-4">
              <i className="fas fa-history mb-3" style={{ fontSize: '30px', color: '#94a3b8' }}></i>
              <h5 className="fw-bold text-dark mb-1">No Estimate History Found</h5>
              <p className="text-muted" style={{ fontSize: '13px' }}>Estimations you create and save will be listed here for quick retrieval and re-downloads.</p>
              <Button size="sm" variant="primary" className="fw-bold mt-2" onClick={() => setActiveSubTab('create')}>
                Create First Estimate
              </Button>
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle border rounded-3 overflow-hidden mb-0">
                <thead className="table-light">
                  <tr style={{ fontSize: '12px', color: '#64748b' }}>
                    <th className="py-3">Estimate No</th>
                    <th>Customer Name</th>
                    <th>Date</th>
                    <th>Grand Total</th>
                    <th>Line Items</th>
                    <th>Prepared By</th>
                    <th className="text-center">Downloads</th>
                    <th className="text-center" style={{ width: '140px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {estimations.map((est) => (
                    <tr key={est.estimateNo} style={{ fontSize: '13.5px' }}>
                      <td className="font-monospace fw-bold text-primary py-3">{est.estimateNo}</td>
                      <td>
                        <span className="fw-semibold text-dark d-block">{est.clientInfo.name}</span>
                        {est.clientInfo.company && <span className="text-muted" style={{ fontSize: '11px' }}>{est.clientInfo.company}</span>}
                      </td>
                      <td>{est.date}</td>
                      <td className="fw-bold text-dark font-monospace">₹{est.financials.grandTotal.toLocaleString('en-IN')}</td>
                      <td>
                        <Badge bg="secondary" className="px-2.5 py-1">
                          {est.items.length} {est.items.length === 1 ? 'item' : 'items'}
                        </Badge>
                      </td>
                      <td>
                        <span className="fw-semibold text-dark">{est.generatedBy || 'Admin'}</span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ borderRadius: '6px' }}
                            onClick={() => handleDownloadPDF(est)}
                            title="Download PDF Estimate"
                          >
                            <i className="fas fa-file-pdf me-1"></i> PDF
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            style={{ borderRadius: '6px' }}
                            onClick={() => handleExportExcel(est)}
                            title="Export Excel Estimate"
                          >
                            <i className="fas fa-file-excel me-1"></i> Excel
                          </Button>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1.5">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ borderRadius: '6px' }}
                            onClick={() => handleEditEstimation(est)}
                            title="Edit Estimate"
                            className="d-flex align-items-center gap-1 px-2.5 py-1"
                          >
                            <i className="fas fa-edit"></i>
                            <span style={{ fontSize: '12px' }}>Edit</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ borderRadius: '6px' }}
                            onClick={() => handleDeleteHistory(est.estimateNo)}
                            title="Delete Estimate"
                            className="px-2 py-1"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
      <CustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onCustomerSaved={(savedCustomer) => {
          setSelectedCustomerIdState(savedCustomer.id);
          setClientName(savedCustomer.username || '');
          setClientCompany(savedCustomer.companyName || '');
          setClientPhone(savedCustomer.phone || '');
          setClientEmail(savedCustomer.email || '');
          setClientAddress(savedCustomer.companyAddress || savedCustomer.shippingAddress || '');
          setClientGstin(savedCustomer.gstNumber || '');
        }}
      />
    </Card>
  );
};

export default EstimationGenerator;
